import React from "react";

import { styled } from "@mui/material";

const TickMark = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  position: "relative",
  minWidth: "clamp(30px, 4vw, 50px)",
  height: "30px",
  // Circle marker centered on line
  "&::before": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    backgroundColor: theme.palette.brand?.red,
    opacity: 0,
    visibility: "hidden",
    zIndex: 2,
  },
  // Tick mark
  "&::after": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "2px",
    height: "8px",
    backgroundColor: "#636363",
    opacity: 1,
    visibility: "visible",
    zIndex: 1,
  },
  "&.selected::before": {
    opacity: 1,
    visibility: "visible",
  },
  "&.selected::after": {
    opacity: 0,
    visibility: "hidden",
  },
  "&.unavailable": {
    cursor: "not-allowed",
    opacity: 0.3,
  },
  "&:hover:not(.selected):not(.unavailable)::before": {
    opacity: 0.6,
    visibility: "visible",
  },
  "&:hover:not(.unavailable)::after": {
    opacity: 0.3,
  },
}));

const TickLabel = styled("div", {
  shouldForwardProp: (prop) =>
    prop !== "selected" && prop !== "projection" && prop !== "used",
})(({ selected, used, theme }) => ({
  fontSize: "0.72em",
  color: selected ? theme.palette.brand?.red : "#000000",
  whiteSpace: "nowrap",
  fontWeight: selected ? "700" : "400",
  position: "absolute",
  bottom: "-1px",
  left: "50%",
  transform: selected
    ? "translateX(-100%) rotate(-45deg)"
    : "translateX(-100%) translateY(-2px) rotate(-45deg)",
  transformOrigin: "bottom right",
  textAlign: "right",
  userSelect: "none",
  pointerEvents: used ? "auto" : "none",
  cursor: used ? "pointer" : "default",
}));

/**
 * Reusable desktop timeline tick (mark + label).
 * Stateless: applies selected/disabled styling and calls onPick.
 */
function TimelineItem({
  originalIndex,
  labelHtml,
  selected,
  available,
  projection,
  onPick,
}) {
  const className = selected ? "selected" : !available ? "unavailable" : "";

  return (
    <TickMark
      key={`${projection ? "proj" : "beob"}-${originalIndex}`}
      data-cy={projection ? "projection-tick" : "observation-tick"}
      className={className}
      onClick={() => {
        if (!available) return;
        onPick(originalIndex);
      }}
    >
      <TickLabel
        selected={selected}
        projection={projection}
        used={available}
        onClick={(e) => {
          e.stopPropagation();
          if (!available) return;
          onPick(originalIndex);
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: labelHtml }} />
      </TickLabel>
    </TickMark>
  );
}

export default TimelineItem;
